import React, { useMemo } from "react";
import { Button, Empty, Switch, Tag, Tooltip, Typography } from "antd";
import {
  BankOutlined,
  WalletOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

const CURRENCY_ORDER = ["MNT", "USD", "EUR", "CNY", "KRW", "JPY"];

function formatAmount(value) {
  const number = Number(value ?? 0);

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

function sortCurrencies(currencies = []) {
  return [...currencies].sort((a, b) => {
    const aCode = a?.currencyCode || "";
    const bCode = b?.currencyCode || "";

    const aIndex = CURRENCY_ORDER.indexOf(aCode);
    const bIndex = CURRENCY_ORDER.indexOf(bCode);

    if (aIndex !== -1 || bIndex !== -1) {
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    }

    return aCode.localeCompare(bCode);
  });
}

function CurrencyBadge({ currency }) {
  const code = currency?.currencyCode || "N/A";
  const wallet = Number(currency?.totalWallet ?? 0);
  const balance = Number(currency?.totalBalance ?? 0);

  const walletClass =
    wallet < 0
      ? "text-red-600"
      : wallet > 0
      ? "text-slate-900"
      : "text-slate-700";

  return (
    <Tooltip
      title={
        <div className="text-sm">
          <div className="flex items-center justify-between gap-6">
            <span className="text-slate-400">Хийсвэр үлдэгдэл</span>
            <span className="font-medium text-white">
              {formatAmount(wallet)}
            </span>
          </div>

          <div className="mt-1 flex items-center justify-between gap-6">
            <span className="text-slate-400">Жинхэнэ үлдэгдэл</span>
            <span className="font-medium text-white">
              {formatAmount(balance)}
            </span>
          </div>
        </div>
      }
    >
      <div className="min-w-[180px] rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
          {code}
        </div>

        <div className={`mt-2 text-[22px] font-semibold leading-6 ${walletClass}`}>
          {formatAmount(wallet)}
        </div>

        <div className="mt-1 text-[16px] leading-5 text-slate-500">
          {formatAmount(balance)}
        </div>
      </div>
    </Tooltip>
  );
}

function TradingHouseListItem({
  item,
  onAccounts,
  onWallets,
  onToggleActive,
  toggling,
  canManage,
}) {
  const institution = item?.institution;
  const isActive = Boolean(institution?.is_active);

  const currencies = useMemo(() => {
    return sortCurrencies(Array.isArray(item?.currencies) ? item.currencies : []);
  }, [item?.currencies]);

  const totalAccounts = useMemo(() => {
    return currencies.reduce(
      (sum, currency) => sum + Number(currency?.accountCount || 0),
      0
    );
  }, [currencies]);

  return (
    <div
      className={`
        rounded-[28px] border px-6 py-6 transition-all hover:border-slate-300
        ${
          isActive
            ? "border-slate-200 bg-white"
            : "border-slate-200 bg-slate-50 opacity-90"
        }
      `}
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
              <BankOutlined className="text-[18px]" />
            </div>

            <div className="min-w-0">
              <div className="text-[24px] font-semibold leading-7 text-slate-900">
                {institution?.name || "Тодорхойгүй трейдинг байгууллага"}
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-md text-slate-500">
                <span>ID {institution?.id ?? "-"}</span>
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span>{currencies.length} валют</span>
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span>{totalAccounts} данс</span>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Tag
                  color={isActive ? "success" : "default"}
                  className="rounded-md border-0 px-2.5 py-[3px] text-[10px] tracking-[0.12em]"
                >
                  {isActive ? "ACTIVE" : "HIDDEN"}
                </Tag>
              </div>
            </div>
          </div>

          {canManage ? (
            <div className="flex shrink-0 flex-col items-end gap-1">
              <Switch
                checked={isActive}
                loading={toggling}
                onChange={(checked) => onToggleActive?.(item, checked)}
              />
              <Text className="text-[11px] text-slate-500">
                {isActive ? "Active" : "Hidden"}
              </Text>
            </div>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-wrap gap-3">
          {currencies.map((currency) => (
            <CurrencyBadge
              key={`${institution?.id}-${currency?.currencyCode}`}
              currency={currency}
            />
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {isActive ? (
            <Button
              icon={<DatabaseOutlined />}
              className="
                h-11 rounded-[18px] border border-slate-300
                bg-white text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.04)]
                hover:!border-slate-400 hover:!bg-slate-50 hover:!text-slate-900
              "
              onClick={(e) => onAccounts?.(item, e)}
            >
              Данснууд
            </Button>
          ) : null}

          {isActive ? (
            <Button
              icon={<WalletOutlined />}
              className="
                h-11 rounded-[18px] border border-slate-200
                bg-slate-50 text-slate-700 shadow-none
                hover:!border-slate-300 hover:!bg-white hover:!text-slate-900
              "
              onClick={(e) => onWallets?.(item, e)}
            >
              Хийсвэр гүйлгээ хийх
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function TradingHouseList({
  items = [],
  onAccounts,
  onWallets,
  onToggleActive,
  togglingInstitutionId,
  canManage = false,
  emptyText = "Трейдинг байгууллага олдсонгүй",
}) {
  if (!items.length) {
    return (
      <div className="rounded-[28px] border border-slate-200 bg-white px-6 py-12">
        <Empty description={emptyText} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {items.map((item) => (
        <TradingHouseListItem
          key={item?.institution?.id}
          item={item}
          onAccounts={onAccounts}
          onWallets={onWallets}
          onToggleActive={onToggleActive}
          toggling={togglingInstitutionId === item?.institution?.id}
          canManage={canManage}
        />
      ))}
    </div>
  );
}